import * as i0 from '@angular/core';
import { Pipe, EventEmitter, Output, Input, ViewChild, Component, forwardRef, NgModule } from '@angular/core';
import { FormGroup, Validators, NG_VALUE_ACCESSOR } from '@angular/forms';
import { htmlId, removeAtIndex } from '@engineering11/utility';
import { uniq } from 'lodash';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import * as i3$1 from '@engineering11/ui-lib/e11-icon';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';
import * as i2 from '@engineering11/ui-lib/e11-modal';
import { E11ModalModule } from '@engineering11/ui-lib/e11-modal';
import * as i3 from '@engineering11/ui-lib/e11-image-cropper';
import { E11ImageCropperModuleModule } from '@engineering11/ui-lib/e11-image-cropper';

/**
 *
 * @param {File} file The file to validate
 * @param {string} accept The accept attribute value joined by comma
 * @returns {boolean} True if the file is valid, false otherwise
 */
function defaultValidateFile(file, accept) {
    if (!accept)
        return true;
    const isValid = accept === '*' ||
        accept
            .replace(/\./g, '')
            .split(',')
            .some((ext) => file.type.includes(ext));
    return isValid || `Invalid file type. Only ${accept} files are allowed.`;
}

const defaultPrecisionMap = {
    bytes: 0,
    KB: 0,
    MB: 1,
    GB: 1,
    TB: 2,
    PB: 2,
};
class FileSizePipe {
    constructor() {
        this.units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    }
    transform(bytes = 0, precision = defaultPrecisionMap) {
        if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes))
            return '?';
        let unitIndex = 0;
        while (bytes >= 1024) {
            bytes /= 1024;
            unitIndex++;
        }
        const unit = this.units[unitIndex];
        if (typeof precision === 'number') {
            return `${bytes.toFixed(+precision)} ${unit}`;
        }
        return `${bytes.toFixed(precision[unit])} ${unit}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FileSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: FileSizePipe, name: "fileSize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FileSizePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'fileSize' }]
        }] });

class E11InputFilePreviewComponent {
    constructor() {
        this.onRemove = new EventEmitter();
        this.onCrop = new EventEmitter();
        this.onImageSave = new EventEmitter();
    }
    set file(value) {
        if (value) {
            this._file = value;
            this._path = URL.createObjectURL(value);
        }
    }
    set path(value) {
        if (value)
            this._path = value;
    }
    get file() {
        return this._file;
    }
    remove(file) {
        if (!this.disabled && !this.loading)
            this.onRemove.emit(file);
    }
    afterCrop(file) {
        if (!this.disabled && !this.loading)
            this.onCrop.emit(file);
    }
    openModal() {
        this.imageCropModal.open();
        this.imageCropper.reflectAllChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFilePreviewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InputFilePreviewComponent, selector: "e11-input-file-preview", inputs: { file: "file", path: "path", disabled: "disabled", loading: "loading" }, outputs: { onRemove: "onRemove", onCrop: "onCrop", onImageSave: "onImageSave" }, host: { classAttribute: "e11-group e11-relative e11-w-20 md:e11-w-32" }, viewQueries: [{ propertyName: "imageCropModal", first: true, predicate: ["imageCropModal"], descendants: true }, { propertyName: "imageCropper", first: true, predicate: ["imageCropper"], descendants: true }], ngImport: i0, template: `
    <div
      class="e11-relative e11-aspect-h-1 e11-aspect-w-1 e11-w-20 md:e11-w-32 e11-overflow-hidden e11-rounded-md lg:e11-aspect-none lg:e11-h-32"
      [ngClass]="{
        'e11-skeleton e11-cursor-wait e11-opacity-25': loading,
        'e11-opacity-75 e11-cursor-not-allowed': disabled,
        'group-hover:e11-opacity-75 e11-bg-gray-200': !loading && !disabled
      }"
    >
      <img
        *ngIf="!file || file.type.includes('image')"
        [src]="_path"
        [alt]="file?.name ?? 'image preview'"
        class="e11-h-32 e11-w-32 e11-object-cover e11-object-center lg:e11-h-32 "
      />
      <span *ngIf="file && !file.type.includes('image')" class="material-icons e11-text-gray e11-text-7xl md:e11-text-9xl ">description</span>
    </div>

    <div *ngIf="file">
      <div class="e11-flex e11-justify-between ">
        <div class="e11-w-32">
          <p href="#" class="e11-mb-0 e11-text-sm e11-truncate e11-overflow-hidden e11-text-gray-700">{{ file.name }}</p>
          <span class="e11-mt-1 e11-text-sm e11-text-gray-500">{{ file.size | fileSize }}</span>
        </div>
      </div>
      <div
        *ngIf="!disabled && !loading"
        class="e11-absolute e11-cursor-pointer e11-hidden  group-hover:e11-block e11-top-0 e11-right-1 e11-h-5 e11-w-5 e11--translate-y-1/2 e11-translate-x-1/2 e11-transform e11-rounded-full e11-bg-white"
        (click)="remove(file)"
      >
        <span class="material-icons e11-text-gray-400 e11-text-[20px]">cancel</span>
      </div>
      <div
        *ngIf="file.type.includes('image') && file.type !== 'image/svg+xml' && !disabled && !loading"
        class="e11-absolute e11-cursor-pointer e11-hidden group-hover:e11-block e11-top-1/3 e11-left-1/2 -e11-translate-x-1/2 -e11-translate-y-1/2"
        (click)="openModal()"
      >
        <span class="material-icons e11-text-white e11-text-[20px]">visibility</span>
      </div>
      <e11-modal #imageCropModal e11AppendTo="body" [showHeader]="false" [showFooter]="false" [closer]="true" [width]="'lg'">
        <div modal-body>
          <e11-image-cropper
            #imageCropper
            imageContainerClassOverrides="e11-h-1/3 e11-max-h-[424px] "
            [imageFile]="file"
            (onImageSave)="onImageSave.emit($event)"
            [showSaveButton]="true"
          >
          </e11-image-cropper>
        </div>
      </e11-modal>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: i3.E11ImageCropperComponent, selector: "e11-image-cropper", inputs: ["imageChangedEvent", "imageURL", "imageBase64", "imageFile", "format", "maintainAspectRatio", "transform", "aspectRatio", "resizeToWidth", "resizeToHeight", "cropperMinWidth", "cropperMinHeight", "cropperStaticWidth", "cropperStaticHeight", "canvasRotation", "initialStepSize", "roundCropper", "onlyScaleDown", "imageQuality", "autoCrop", "backgroundColor", "containWithinAspectRatio", "hideResizeSquares", "imageContainerClassOverrides", "cropper", "scale", "alignImage", "disabled", "showSaveButton", "rotation"], outputs: ["imageCropped", "startCropImage", "imageLoaded", "cropperReady", "loadImageFailed", "onImageSave"] }, { kind: "pipe", type: FileSizePipe, name: "fileSize" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFilePreviewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-input-file-preview',
                    template: `
    <div
      class="e11-relative e11-aspect-h-1 e11-aspect-w-1 e11-w-20 md:e11-w-32 e11-overflow-hidden e11-rounded-md lg:e11-aspect-none lg:e11-h-32"
      [ngClass]="{
        'e11-skeleton e11-cursor-wait e11-opacity-25': loading,
        'e11-opacity-75 e11-cursor-not-allowed': disabled,
        'group-hover:e11-opacity-75 e11-bg-gray-200': !loading && !disabled
      }"
    >
      <img
        *ngIf="!file || file.type.includes('image')"
        [src]="_path"
        [alt]="file?.name ?? 'image preview'"
        class="e11-h-32 e11-w-32 e11-object-cover e11-object-center lg:e11-h-32 "
      />
      <span *ngIf="file && !file.type.includes('image')" class="material-icons e11-text-gray e11-text-7xl md:e11-text-9xl ">description</span>
    </div>

    <div *ngIf="file">
      <div class="e11-flex e11-justify-between ">
        <div class="e11-w-32">
          <p href="#" class="e11-mb-0 e11-text-sm e11-truncate e11-overflow-hidden e11-text-gray-700">{{ file.name }}</p>
          <span class="e11-mt-1 e11-text-sm e11-text-gray-500">{{ file.size | fileSize }}</span>
        </div>
      </div>
      <div
        *ngIf="!disabled && !loading"
        class="e11-absolute e11-cursor-pointer e11-hidden  group-hover:e11-block e11-top-0 e11-right-1 e11-h-5 e11-w-5 e11--translate-y-1/2 e11-translate-x-1/2 e11-transform e11-rounded-full e11-bg-white"
        (click)="remove(file)"
      >
        <span class="material-icons e11-text-gray-400 e11-text-[20px]">cancel</span>
      </div>
      <div
        *ngIf="file.type.includes('image') && file.type !== 'image/svg+xml' && !disabled && !loading"
        class="e11-absolute e11-cursor-pointer e11-hidden group-hover:e11-block e11-top-1/3 e11-left-1/2 -e11-translate-x-1/2 -e11-translate-y-1/2"
        (click)="openModal()"
      >
        <span class="material-icons e11-text-white e11-text-[20px]">visibility</span>
      </div>
      <e11-modal #imageCropModal e11AppendTo="body" [showHeader]="false" [showFooter]="false" [closer]="true" [width]="'lg'">
        <div modal-body>
          <e11-image-cropper
            #imageCropper
            imageContainerClassOverrides="e11-h-1/3 e11-max-h-[424px] "
            [imageFile]="file"
            (onImageSave)="onImageSave.emit($event)"
            [showSaveButton]="true"
          >
          </e11-image-cropper>
        </div>
      </e11-modal>
    </div>
  `,
                    host: {
                        class: 'e11-group e11-relative e11-w-20 md:e11-w-32',
                    },
                }]
        }], propDecorators: { imageCropModal: [{
                type: ViewChild,
                args: ['imageCropModal']
            }], imageCropper: [{
                type: ViewChild,
                args: ['imageCropper']
            }], file: [{
                type: Input
            }], path: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], onRemove: [{
                type: Output
            }], onCrop: [{
                type: Output
            }], onImageSave: [{
                type: Output
            }] } });

class E11InputFileComponent {
    constructor() {
        this.multi = false;
        this.validateFile = defaultValidateFile;
        this.autoCrop = false;
        this.parentForm = new FormGroup({});
        this.hasError = false;
        this.required = false;
        this.disabled = false;
        this.loading = false;
        this._accept = '*';
        this.labelClassOverrides = '';
        this.inputClass = '';
        this.containerClass = '';
        this.labelTextOptional = '(Optional)';
        this.id = htmlId('e11-input-file');
        this._files = [];
        this._defaultUrls = [];
        this.acceptDisplay = 'All files';
        this.onChange = (_) => { };
        this.onTouch = () => { };
    }
    set accept(value) {
        const uniqValue = uniq(value);
        if (uniqValue.length === 0) {
            this._accept = '*';
            this.acceptDisplay = 'All files';
            return;
        }
        this._accept = uniqValue.join(',');
        this.acceptDisplay = this.joinWithOr(uniqValue.map((type) => {
            return type.replace('/*', '').replace('.', '').toUpperCase();
        }), 5);
    }
    set defaultPreviewUrls(urls) {
        this._defaultUrls = urls;
    }
    set files(files) {
        this._files = files;
    }
    get files() {
        return this._files;
    }
    writeValue(files) {
        if (files) {
            this.files = files || [];
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value?.length == 0 || control.value === null)) {
            this.hasError = this.hasError ? true : control.touched;
            return { required: true };
        }
        this.hasError = false;
        return null;
    }
    onFileSelected(event) {
        const input = event.target;
        if (!input.files?.length) {
            return;
        }
        this.handleFiles(input.files);
        input.value = ''; // Reset the input
    }
    handleFiles(fileList) {
        const files = Array.from(fileList);
        const allValid = files.every((file) => {
            const isValid = this.validateFile(file, this._accept);
            if (isValid !== true) {
                this.errorMessage = typeof isValid === 'string' ? isValid : 'Invalid file';
                return false;
            }
            return true;
        });
        if (allValid) {
            this.files = this.multi ? [...this.files, ...files] : [...files];
            this._defaultUrls = undefined;
            this.onChange(this.files);
            this.errorMessage = '';
            this.hasError = false;
        }
        else {
            this.onChange(null);
        }
    }
    saveFile(file, index) {
        this.files[index] = file;
        this.onChange(this.files);
    }
    removeFile(index) {
        this.files = removeAtIndex(this.files, index);
        this.onChange(this.files.length > 0 ? this.files : null);
        this.hasError = this.required && this.files.length === 0;
    }
    onDragOver(event) {
        event.preventDefault();
        // Add any additional styling for drag over
    }
    onDrop(event) {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (files) {
            this.handleFiles(files);
        }
    }
    onDragLeave(event) {
        event.preventDefault();
        // Remove any additional styling for drag leave
    }
    ngOnInit() {
        this.initFormMethods();
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        const formControl = this.parentForm?.controls[this.formControlName];
        if (!formControl)
            return;
        this.required = formControl.hasValidator(Validators.required);
    }
    joinWithOr(array, limit) {
        if (array.length <= 1) {
            return array.join('');
        }
        if (array.length <= limit) {
            const allButLast = array.slice(0, -1).join(', ');
            const last = array.slice(-1);
            return `${allButLast} or ${last}`;
        }
        const allButLast = array.slice(0, limit).join(', ');
        return `${allButLast} more`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InputFileComponent, selector: "e11-input-file", inputs: { multi: "multi", errorMessage: "errorMessage", validateFile: "validateFile", autoCrop: "autoCrop", label: "label", formControlName: "formControlName", parentForm: "parentForm", hasError: "hasError", required: "required", disabled: "disabled", loading: "loading", accept: "accept", labelClassOverrides: "labelClassOverrides", inputClass: "inputClass", containerClass: "containerClass", tooltipConfig: "tooltipConfig", labelTextOptional: "labelTextOptional", defaultPreviewUrls: "defaultPreviewUrls", id: "id" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InputFileComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"e11-relative {{ containerClass }} \">\n  <e11-input-label\n    [for]=\"id\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n    [labelClassOverrides]=\"labelClassOverrides\"\n  ></e11-input-label>\n  <label\n    [for]=\"id\"\n    (dragover)=\"onDragOver($event)\"\n    (drop)=\"onDrop($event)\"\n    (dragleave)=\"onDragLeave($event)\"\n    class=\"e11-flex e11-flex-col e11-items-center e11-justify-center e11-w-full e11-border-2 e11-border-dashed e11-rounded-lg e11-bg-gray-50 hover:e11-bg-gray-100 {{\n      inputClass\n    }}\"\n    [ngClass]=\"{\n      'e11-skeleton e11-cursor-wait': loading,\n      'e11-cursor-pointer': !disabled,\n      'e11-cursor-not-allowed': disabled,\n      'e11-border-gray-300': !hasError,\n      'e11-border-skin-error': hasError\n    }\"\n  >\n    <div\n      class=\"e11-flex e11-flex-col e11-items-center e11-justify-center e11-pt-5 e11-pb-6\"\n      [ngClass]=\"{\n        'e11-text-gray-500 dark:e11-text-gray-400': !hasError,\n        'e11-text-skin-error': hasError\n      }\"\n    >\n      <span\n        class=\"material-icons-outlined e11-w-10 e11-mb-3\"\n        [ngClass]=\"{\n          ' e11-text-gray-400': !hasError,\n          'e11-text-skin-error': hasError\n        }\"\n        >cloud_upload</span\n      >\n      <p class=\"e11-mb-2 e11-text-sm\"><span class=\"e11-font-semibold\">Click to upload</span> or drag and drop</p>\n      <p class=\"e11-text-xs\">{{ acceptDisplay }} (MAX. 800x400px)</p>\n    </div>\n    <input\n      type=\"file\"\n      [id]=\"id\"\n      [attr.multiple]=\"multi ? '' : null\"\n      [accept]=\"_accept\"\n      class=\"e11-hidden\"\n      [disabled]=\"loading || disabled\"\n      (change)=\"onFileSelected($event)\"\n    />\n  </label>\n\n  <p *ngIf=\"errorMessage\" class=\"e11-text-skin-error e11-text-sm\">{{ errorMessage }}</p>\n\n  <div *ngIf=\"(_files?.length ?? 0) > 0 || (_defaultUrls?.length ?? 0) > 0\" class=\"e11-flex e11-mt-2 e11-gap-2 e11-items-center\">\n    <div class=\"e11-flex e11-gap-2 e11-items-center e11-flex-wrap\">\n      <!-- FOR UPLOADED FILES -->\n      <ng-container *ngIf=\"_files && _files.length > 0\">\n        <e11-input-file-preview\n          *ngFor=\"let _file of _files; let i = index\"\n          [file]=\"_files[i]\"\n          (onRemove)=\"removeFile(i)\"\n          (onImageSave)=\"saveFile($event, i)\"\n          [loading]=\"loading\"\n          [disabled]=\"disabled\"\n        ></e11-input-file-preview>\n      </ng-container>\n\n      <!-- FOR DEFAULT URLS -->\n      <ng-container *ngIf=\"_defaultUrls && _defaultUrls.length > 0\">\n        <e11-input-file-preview\n          *ngFor=\"let _defaultUrl of _defaultUrls; let i = index\"\n          [path]=\"_defaultUrls[i]\"\n          [loading]=\"loading\"\n          [disabled]=\"disabled\"\n        ></e11-input-file-preview>\n      </ng-container>\n    </div>\n  </div>\n\n  <div *ngIf=\"hasError\" class=\"e11-absolute e11-top-0 e11-right-0 e11-flex e11-items-center e11-pointer-events-none\">\n    <e11-icon-error></e11-icon-error>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "description", "tooltipConfig"] }, { kind: "component", type: i3$1.E11IconErrorComponent, selector: "e11-icon-error", inputs: ["classOverrides"] }, { kind: "component", type: E11InputFilePreviewComponent, selector: "e11-input-file-preview", inputs: ["file", "path", "disabled", "loading"], outputs: ["onRemove", "onCrop", "onImageSave"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-input-file', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InputFileComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"e11-relative {{ containerClass }} \">\n  <e11-input-label\n    [for]=\"id\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n    [labelClassOverrides]=\"labelClassOverrides\"\n  ></e11-input-label>\n  <label\n    [for]=\"id\"\n    (dragover)=\"onDragOver($event)\"\n    (drop)=\"onDrop($event)\"\n    (dragleave)=\"onDragLeave($event)\"\n    class=\"e11-flex e11-flex-col e11-items-center e11-justify-center e11-w-full e11-border-2 e11-border-dashed e11-rounded-lg e11-bg-gray-50 hover:e11-bg-gray-100 {{\n      inputClass\n    }}\"\n    [ngClass]=\"{\n      'e11-skeleton e11-cursor-wait': loading,\n      'e11-cursor-pointer': !disabled,\n      'e11-cursor-not-allowed': disabled,\n      'e11-border-gray-300': !hasError,\n      'e11-border-skin-error': hasError\n    }\"\n  >\n    <div\n      class=\"e11-flex e11-flex-col e11-items-center e11-justify-center e11-pt-5 e11-pb-6\"\n      [ngClass]=\"{\n        'e11-text-gray-500 dark:e11-text-gray-400': !hasError,\n        'e11-text-skin-error': hasError\n      }\"\n    >\n      <span\n        class=\"material-icons-outlined e11-w-10 e11-mb-3\"\n        [ngClass]=\"{\n          ' e11-text-gray-400': !hasError,\n          'e11-text-skin-error': hasError\n        }\"\n        >cloud_upload</span\n      >\n      <p class=\"e11-mb-2 e11-text-sm\"><span class=\"e11-font-semibold\">Click to upload</span> or drag and drop</p>\n      <p class=\"e11-text-xs\">{{ acceptDisplay }} (MAX. 800x400px)</p>\n    </div>\n    <input\n      type=\"file\"\n      [id]=\"id\"\n      [attr.multiple]=\"multi ? '' : null\"\n      [accept]=\"_accept\"\n      class=\"e11-hidden\"\n      [disabled]=\"loading || disabled\"\n      (change)=\"onFileSelected($event)\"\n    />\n  </label>\n\n  <p *ngIf=\"errorMessage\" class=\"e11-text-skin-error e11-text-sm\">{{ errorMessage }}</p>\n\n  <div *ngIf=\"(_files?.length ?? 0) > 0 || (_defaultUrls?.length ?? 0) > 0\" class=\"e11-flex e11-mt-2 e11-gap-2 e11-items-center\">\n    <div class=\"e11-flex e11-gap-2 e11-items-center e11-flex-wrap\">\n      <!-- FOR UPLOADED FILES -->\n      <ng-container *ngIf=\"_files && _files.length > 0\">\n        <e11-input-file-preview\n          *ngFor=\"let _file of _files; let i = index\"\n          [file]=\"_files[i]\"\n          (onRemove)=\"removeFile(i)\"\n          (onImageSave)=\"saveFile($event, i)\"\n          [loading]=\"loading\"\n          [disabled]=\"disabled\"\n        ></e11-input-file-preview>\n      </ng-container>\n\n      <!-- FOR DEFAULT URLS -->\n      <ng-container *ngIf=\"_defaultUrls && _defaultUrls.length > 0\">\n        <e11-input-file-preview\n          *ngFor=\"let _defaultUrl of _defaultUrls; let i = index\"\n          [path]=\"_defaultUrls[i]\"\n          [loading]=\"loading\"\n          [disabled]=\"disabled\"\n        ></e11-input-file-preview>\n      </ng-container>\n    </div>\n  </div>\n\n  <div *ngIf=\"hasError\" class=\"e11-absolute e11-top-0 e11-right-0 e11-flex e11-items-center e11-pointer-events-none\">\n    <e11-icon-error></e11-icon-error>\n  </div>\n</div>\n" }]
        }], propDecorators: { multi: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], validateFile: [{
                type: Input
            }], autoCrop: [{
                type: Input
            }], label: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], hasError: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], accept: [{
                type: Input
            }], labelClassOverrides: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], labelTextOptional: [{
                type: Input
            }], defaultPreviewUrls: [{
                type: Input
            }], id: [{
                type: Input
            }] } });

class E11InputFileModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileModule, declarations: [E11InputFileComponent, E11InputFilePreviewComponent, FileSizePipe], imports: [CommonModule, E11ModalModule, E11ImageCropperModuleModule, E11InputLabelModule, E11IconModule], exports: [E11InputFileComponent, E11InputFilePreviewComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileModule, imports: [CommonModule, E11ModalModule, E11ImageCropperModuleModule, E11InputLabelModule, E11IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputFileModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, E11ModalModule, E11ImageCropperModuleModule, E11InputLabelModule, E11IconModule],
                    exports: [E11InputFileComponent, E11InputFilePreviewComponent],
                    declarations: [E11InputFileComponent, E11InputFilePreviewComponent, FileSizePipe],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11InputFileComponent, E11InputFileModule, E11InputFilePreviewComponent };
